import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Search from '@mui/icons-material/Search';
import AutoAwesome from '@mui/icons-material/AutoAwesome';
import Add from '@mui/icons-material/Add';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Dashboard from '@mui/icons-material/Dashboard';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientService } from '../../services/clientService';
import { projectService } from '../../services/projectService';
import { useAuth } from '../../contexts/AuthContext';
import { useProjectWorkflow } from '../../contexts/ProjectWorkflowContext';
import { clientInitials, projectColor } from '../../types/projectWorkflow';
import { Client, Project, PaginatedResponse } from '../../types';
import {
  agencyClientDisplayName,
  isAgencySelfProfileClient,
} from '../../utils/agencySelfProfile';
import { markPreferDashboard } from '../../utils/homeLaunch';

interface ClientProjectPanelProps {
  open: boolean;
  onToggle: () => void;
  onCreateProject: (clientId?: string) => void;
  mode: 'admin' | 'client';
}

const ClientProjectPanel: React.FC<ClientProjectPanelProps> = ({
  open,
  onToggle,
  onCreateProject,
  mode,
}) => {
  const { user, isClient, isAgency, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, selectProject, clearProject, refreshProjectsKey, refreshClientsKey, panelFocusClientId, clearPanelFocusClient, homeLaunchKey } =
    useProjectWorkflow();
  const [search, setSearch] = useState('');

  // Browsers often autofill login email into the first text/search field in the shell.
  // Clear bogus email fills and reset when leaving the project workflow.
  useEffect(() => {
    const onBilling =
      location.pathname.startsWith('/billing') || location.pathname.startsWith('/invoices');
    if (onBilling || /@/.test(search)) {
      setSearch('');
    }
    // Only react to route changes for the billing clear; email clear also when search mutates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (/@/.test(search)) setSearch('');
  }, [search]);

  // Persisted expand/collapse state per client (true = expanded). Defaults to a
  // single open client; the user's manual changes are remembered across
  // navigation and reloads.
  const EXPANDED_STORAGE_KEY = 'wf-expanded-clients';
  const [expandedClients, setExpandedClients] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(EXPANDED_STORAGE_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  });

  const getClientDisplayName = (client: Client): string =>
    agencyClientDisplayName(client, user?.email, isAgency);

  const isAgencySelfProfile = (client: Client): boolean =>
    isAgencySelfProfileClient(client, user?.email, isAgency);

  const clientIdFilter = isClient ? user?.clientId : undefined;

  const { data: clientsData, isLoading: clientsLoading } = useQuery<PaginatedResponse<Client>>({
    queryKey: ['wf-clients', clientIdFilter, refreshClientsKey],
    queryFn: () =>
      clientService.getClients({
        limit: 500,
      }),
    enabled: mode === 'admin',
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  const { data: projectsData, isLoading: projectsLoading } = useQuery<PaginatedResponse<Project>>({
    queryKey: ['wf-projects', clientIdFilter, refreshProjectsKey],
    queryFn: () =>
      projectService.getProjects({
        limit: 500,
        ...(clientIdFilter ? { clientId: clientIdFilter } : {}),
      }),
    enabled: mode === 'client' || mode === 'admin',
    staleTime: 0,
    placeholderData: (prev) => prev,
  });

  const clients: Client[] = useMemo(() => {
    if (mode === 'client' && user?.clientId) {
      return [
        {
          id: user.clientId,
          name: 'My account',
          email: user.email || '',
          percentage: 100,
          status: 'ACTIVE',
          createdAt: '',
          updatedAt: '',
        } as Client,
      ];
    }
    return (clientsData?.data || []) as Client[];
  }, [mode, clientsData, user]);

  const projectsByClient = useMemo(() => {
    const map: Record<string, Project[]> = {};
    const all = (projectsData?.data || []) as Project[];
    all.forEach((p) => {
      const cid = p.clientId || p.client?.id || '';
      if (!map[cid]) map[cid] = [];
      map[cid].push(p);
    });
    return map;
  }, [projectsData]);

  // Agency: only show "My Projects" (former CLIENT profile) when it still has projects.
  const sidebarClients = useMemo(() => {
    return clients.filter((c) => {
      if (!isAgencySelfProfile(c)) return true;
      return (projectsByClient[c.id]?.length ?? 0) > 0;
    });
  }, [clients, projectsByClient, isAgency, user?.email]);


  // Rank how well a name matches the query, like a normal text search:
  //   0 = name starts with the query ("Allen, Allen…" for "allen")
  //   1 = a word inside the name starts with the query ("Rosenblum Allen Law")
  //   2 = query appears anywhere else in the name
  //   3 = no match
  const matchRank = (name: string, q: string): number => {
    const n = name.toLowerCase();
    if (n.startsWith(q)) return 0;
    const idx = n.indexOf(q);
    if (idx === -1) return 3;
    const prev = n[idx - 1];
    return prev && !/[a-z0-9]/.test(prev) ? 1 : 2;
  };

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sidebarClients;
    // Score each client by its best match (own name or any project name),
    // then sort so the strongest match comes first — that's also the one
    // that auto-expands while searching.
    return sidebarClients
      .map((c) => {
        const displayName = getClientDisplayName(c);
        const projs = projectsByClient[c.id] || [];
        const best = Math.min(
          matchRank(displayName, q),
          matchRank(c.name, q),
          ...projs.map((p) => matchRank(p.projectName, q))
        );
        return { client: c, rank: best };
      })
      .filter((e) => e.rank < 3)
      .sort((a, b) => a.rank - b.rank)
      .map((e) => e.client);
  }, [sidebarClients, search, projectsByClient, isAgency, user?.email]);

  // Returns the projects to display for a client given the current search.
  // If the client's own name matches, show all its projects; otherwise show
  // only matching projects, best matches first.
  const getVisibleProjects = (client: Client): Project[] => {
    const projs = projectsByClient[client.id] || [];
    const q = search.trim().toLowerCase();
    if (!q) return projs;
    const displayName = getClientDisplayName(client);
    if (displayName.toLowerCase().includes(q) || client.name.toLowerCase().includes(q)) return projs;
    return projs
      .filter((p) => matchRank(p.projectName, q) < 3)
      .sort((a, b) => matchRank(a.projectName, q) - matchRank(b.projectName, q));
  };

  // Client mode shows only the signed-in client's projects (no other clients),
  // filtered by the search term against the project name, best matches first.
  const clientProjects = useMemo(() => {
    const all = projectsByClient[user?.clientId || ''] || [];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all
      .filter((p) => matchRank(p.projectName, q) < 3)
      .sort((a, b) => matchRank(a.projectName, q) - matchRank(b.projectName, q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsByClient, user, search]);

  const toggleClient = (id: string) => {
    setExpandedClients((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Remember the user's expand/collapse choices.
  useEffect(() => {
    try {
      localStorage.setItem(EXPANDED_STORAGE_KEY, JSON.stringify(expandedClients));
    } catch {
      /* ignore storage errors */
    }
  }, [expandedClients]);

  // After client/project creation, expand that client's accordion once it is in the list.
  useEffect(() => {
    if (!panelFocusClientId) return;
    const exists = sidebarClients.some((c) => c.id === panelFocusClientId);
    if (!exists) return;
    setExpandedClients({ [panelFocusClientId]: true });
    clearPanelFocusClient();
  }, [panelFocusClientId, sidebarClients, clearPanelFocusClient]);

  // First load (admin mode): if the user has no saved preference yet, expand
  // only one client — the selected project's client if any, otherwise the first.
  const didInitExpandRef = useRef(false);
  useEffect(() => {
    if (mode !== 'admin' || didInitExpandRef.current || sidebarClients.length === 0) return;
    didInitExpandRef.current = true;
    const hasSavedPreference = Object.keys(expandedClients).length > 0;
    if (hasSavedPreference) return;
    const defaultClientId =
      (selectedProject && sidebarClients.find((c) => c.id === selectedProject.clientId)?.id) ||
      sidebarClients[0].id;
    setExpandedClients({ [defaultClientId]: true });
  }, [mode, sidebarClients, selectedProject, expandedClients]);

  // Home click / post-login: open top project.
  // Super Admin → Order Status; Client / Agency / Admin → Explore Sites.
  // Do not gate on `open` — panelOpen may still be false in the same tick as homeLaunchKey.
  const lastHomeLaunchRef = useRef(0);
  useEffect(() => {
    if (!homeLaunchKey || homeLaunchKey === lastHomeLaunchRef.current) return;

    const projectsReady = !projectsLoading;
    const clientsReady = mode === 'client' || !clientsLoading;
    if (!projectsReady || !clientsReady) return;

    lastHomeLaunchRef.current = homeLaunchKey;
    const homeStep = isSuperAdmin ? 'status' : 'sites';

    if (mode === 'client') {
      const top = clientProjects[0];
      if (top) {
        selectProject(top, 'My account', 0, homeStep);
      } else {
        clearProject();
        navigate('/dashboard');
      }
      return;
    }

    for (let cIdx = 0; cIdx < sidebarClients.length; cIdx++) {
      const client = sidebarClients[cIdx];
      const projs = projectsByClient[client.id] || [];
      if (projs.length > 0) {
        setExpandedClients({ [client.id]: true });
        selectProject(projs[0], getClientDisplayName(client), 0, homeStep);
        return;
      }
    }

    clearProject();
    navigate('/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    homeLaunchKey,
    mode,
    isSuperAdmin,
    projectsLoading,
    clientsLoading,
    clientProjects,
    sidebarClients,
    projectsByClient,
  ]);

  const handleSelectProject = (project: Project, clientName: string, idx: number) => {
    // Super Admin: always open Order Status for a client project (not Explore Sites).
    selectProject(project, clientName, idx, isSuperAdmin ? 'status' : undefined);
  };

  const loading = mode === 'admin'
    ? clientsLoading && sidebarClients.length === 0
    : projectsLoading && clientProjects.length === 0;

  // Always mount the panel (HTML design): closed = width 0. Re-open via topbar
  // sidebar toggle — no leftover blue chip column when collapsed.
  return (
    <aside className={`wf-expand-panel${open ? '' : ' closed'}`} aria-hidden={!open}>
      <div className="wf-panel-header">
        <span className="wf-panel-header-title">
          {mode === 'client' ? 'My Projects' : 'Clients & Projects'}
        </span>
        <button
          type="button"
          className="wf-panel-chev"
          onClick={onToggle}
          aria-label={open ? 'Collapse panel' : 'Expand panel'}
          title={open ? 'Close projects panel' : 'Open projects panel'}
        >
          {open ? <ChevronLeft fontSize="small" /> : <ChevronRight fontSize="small" />}
        </button>
      </div>
      <div className="wf-panel-search">
        <Search fontSize="small" />
        <input
          type="search"
          name="wf-client-project-search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          inputMode="search"
          placeholder={mode === 'client' ? 'Search projects…' : 'Search clients, projects…'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="wf-panel-scroll">
        {loading && (
          <div className="wf-panel-loading">Loading…</div>
        )}
        <button
          type="button"
          className={`wf-project-row${location.pathname === '/dashboard' && !selectedProject ? ' on' : ''}`}
          style={{ margin: '6px 6px 2px' }}
          onClick={() => {
            markPreferDashboard();
            clearProject();
            navigate('/dashboard');
          }}
        >
          <Dashboard
            sx={{
              fontSize: 14,
              color:
                location.pathname === '/dashboard' && !selectedProject
                  ? 'var(--accent)'
                  : 'var(--text-muted)',
              flexShrink: 0,
            }}
          />
          <span
            className="wf-project-name"
            style={
              location.pathname === '/dashboard' && !selectedProject
                ? { color: 'var(--accent)', fontWeight: 500 }
                : undefined
            }
          >
            Dashboard
          </span>
        </button>
        <div style={{ height: 1, background: 'var(--border)', margin: '6px 12px 4px' }} />
        {mode === 'client' && !loading && (
          <div className="wf-client-section">
            {clientProjects.length === 0 ? (
              <div className="wf-panel-empty-row">
                <span className="wf-panel-empty-sm">
                  {search.trim() ? 'No projects match your search.' : 'No projects'}
                </span>
                {!search.trim() && (
                  <button
                    type="button"
                    className="wf-panel-add-project-btn"
                    title="Create project"
                    aria-label="Create project"
                    onClick={() => onCreateProject(user?.clientId)}
                  >
                    <Add sx={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
            ) : (
              clientProjects.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  className={`wf-project-row${selectedProject?.id === p.id ? ' on' : ''}`}
                  onClick={() => handleSelectProject(p, 'My account', idx)}
                >
                  <span className="wf-project-dot" style={{ background: projectColor(idx) }} />
                  <span className="wf-project-name">{p.projectName}</span>
                  {p.placementCount != null && (
                    <span className="wf-project-badge">{p.placementCount}</span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
        {mode === 'admin' &&
          !loading &&
          filteredClients.map((client, cIdx) => {
            const projs = getVisibleProjects(client);
            const displayName = getClientDisplayName(client);
            // While searching, only the first match opens; the rest stay collapsed.
            const expanded = search.trim() ? cIdx === 0 : expandedClients[client.id] === true;
            return (
              <div key={client.id} className="wf-client-block">
                <button
                  type="button"
                  className="wf-client-row"
                  onClick={() => toggleClient(client.id)}
                >
                  <span
                    className="wf-client-av"
                    style={{ background: projectColor(cIdx) }}
                  >
                    {clientInitials(displayName)}
                  </span>
                  <span className="wf-client-name">{displayName}</span>
                  <ExpandMore
                    className={`wf-client-chev${expanded ? ' open' : ''}`}
                    fontSize="small"
                  />
                </button>
                {expanded && (
                  <div className="wf-client-projects">
                    {projs.length === 0 ? (
                      <div className="wf-panel-empty-row">
                        <span className="wf-panel-empty-sm">No projects</span>
                        {!search.trim() && (
                          <button
                            type="button"
                            className="wf-panel-add-project-btn"
                            title={`Create project for ${displayName}`}
                            aria-label={`Create project for ${displayName}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              onCreateProject(client.id);
                            }}
                          >
                            <Add sx={{ fontSize: 14 }} />
                          </button>
                        )}
                      </div>
                    ) : (
                      projs.map((p, pIdx) => (
                        <button
                          key={p.id}
                          type="button"
                          className={`wf-project-row${selectedProject?.id === p.id ? ' on' : ''}`}
                          onClick={() => handleSelectProject(p, displayName, pIdx + cIdx)}
                        >
                          <span
                            className="wf-project-dot"
                            style={{ background: projectColor(pIdx + cIdx) }}
                          />
                          <span className="wf-project-name">{p.projectName}</span>
                          {p.placementCount != null && p.placementCount > 0 && (
                            <span className="wf-project-badge">{p.placementCount}</span>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
      <button type="button" className="wf-create-project-btn" onClick={() => onCreateProject()}>
        <AutoAwesome fontSize="small" />
        Create Project
      </button>
    </aside>
  );
};

export default ClientProjectPanel;
